/**
 * Maps HTTP status codes to human-readable error messages
 */
export const getErrorMessage = (error: any): string => {
  // If there's a custom message from the server, prefer it
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error?.response?.data?.error) {
    // Check if it's already a user-friendly message
    const serverError = error.response.data.error;
    
    // If it's a technical error message, try to convert it
    if (serverError.includes('status code') || serverError.includes('Request failed')) {
      // Fall through to status code mapping
    } else {
      // Server provided a custom error message, use it
      return serverError;
    }
  }

  const status = error?.response?.status;
  
  if (!status) {
    // Network error or no response
    if (error?.message?.includes('Network Error') || error?.code === 'ERR_NETWORK') {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    if (error?.message?.includes('timeout')) {
      return 'The request took too long to complete. Please try again.';
    }
    return error?.message || 'An unexpected error occurred. Please try again.';
  }

  // Map status codes to user-friendly messages
  const statusMessages: Record<number, string> = {
    400: 'Invalid request. Please check your input and try again.',
    401: 'Your session has expired. Please log in again.',
    402: 'Payment required. Please check your account balance or subscription status.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource was not found.',
    409: 'This action conflicts with existing data. The item may already exist or be in use.',
    422: 'The information provided is invalid. Please check your input and try again.',
    429: 'Too many requests. Please wait a moment and try again.',
    500: 'An internal server error occurred. Please try again later or contact support if the problem persists.',
    502: 'The server is temporarily unavailable. Please try again in a few moments.',
    503: 'The service is temporarily unavailable. Please try again later.',
    504: 'The request timed out. Please try again.',
  };

  // Get the message for this status code, or a generic one
  const message = statusMessages[status];
  
  if (message) {
    return message;
  }

  // For unhandled status codes, provide a generic message
  if (status >= 500) {
    return 'A server error occurred. Please try again later or contact support if the problem persists.';
  }
  
  if (status >= 400) {
    return 'An error occurred. Please check your input and try again.';
  }

  return 'An unexpected error occurred. Please try again.';
};

/**
 * Gets a user-friendly error message with context
 * @param error - The error object from axios or fetch
 * @param context - Optional context about what operation was being performed
 * @returns A human-readable error message
 */
export const getErrorMessageWithContext = (error: any, context?: string): string => {
  const baseMessage = getErrorMessage(error);
  
  if (context) {
    return `${context}: ${baseMessage}`;
  }
  
  return baseMessage;
};

/**
 * Common error messages for specific operations
 * Messages can be strings or functions that take the error object and return a string
 */
export const OperationErrorMessages: {
  [key: string]: {
    [status: number]: string | ((error: any) => string);
  };
} = {
  createProperty: {
    409: 'A property with this address already exists. Please check the address or archive the existing property if you want to create a new one.',
    400: 'Please fill in all required fields correctly.',
  },
  updateProperty: {
    404: 'The property you are trying to update was not found.',
    403: 'You do not have permission to update this property.',
  },
  deleteProperty: {
    404: 'The property you are trying to delete was not found.',
    403: 'You do not have permission to delete this property.',
  },
  generateAgreement: {
    402: 'Insufficient credits to generate the agreement. Please top up your agency credits.',
    400: (error: any) =>
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      'Unable to generate the agreement. Please check that all required information is provided.',
  },
  uploadFile: {
    413: (error: any) => {
      // Check if the error response includes maxSizeMB
      const maxSizeMB = error?.response?.data?.maxSizeMB || 10;
      return `The file is too large. Maximum file size is ${maxSizeMB}MB. Please choose a smaller file.`;
    },
    400: 'Invalid file type. Please check the file format.',
  },
};

/**
 * Gets an operation-specific error message if available, otherwise falls back to generic message
 */
export const getOperationErrorMessage = (
  error: any,
  operation: keyof typeof OperationErrorMessages
): string => {
  const status = error?.response?.status;
  const operationMessages = OperationErrorMessages[operation];
  
  if (status && operationMessages[status]) {
    const message = operationMessages[status];
    // Handle function messages (for dynamic error messages)
    if (typeof message === 'function') {
      return message(error);
    }
    return message as string;
  }
  
  return getErrorMessage(error);
};
